import { Link } from "react-router-dom";
import { Phone, MessageSquare, Mail, ArrowUpRight } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { Reveal, SectionLabel } from "@/components/Reveal";
import { useBusinessInfo } from "@/lib/useBusinessInfo";

export const Contact = () => {
  const { t } = useLanguage();
  const biz = useBusinessInfo();

  const actions = [
    {
      key: "call",
      icon: Phone,
      label: t.contact.call,
      sub: biz.phone,
      href: biz.phoneHref,
      external: true,
    },
    {
      key: "text",
      icon: MessageSquare,
      label: t.contact.text,
      sub: t.contact.textSub,
      href: biz.smsHref,
      external: true,
    },
    {
      key: "email",
      icon: Mail,
      label: t.contact.email,
      sub: biz.email,
      href: biz.emailHref,
      external: true,
    },
    {
      key: "bid",
      icon: ArrowUpRight,
      label: t.contact.bid,
      sub: t.contact.bidSub,
      href: "/request-a-bid",
      external: false,
    },
  ];

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="border-t border-white/10 bg-[#0d0d0d] py-24 md:py-36"
    >
      <div className="mx-auto max-w-[1600px] px-5 md:px-10">
        <Reveal>
          <SectionLabel text={t.contact.label} testId="contact-label" />
          <h2
            data-testid="contact-heading"
            className="mt-6 max-w-4xl font-display text-4xl font-black uppercase leading-[0.95] tracking-tight text-white md:text-7xl"
          >
            {t.contact.heading}
          </h2>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-neutral-400 md:text-lg">
            {t.contact.sub}
          </p>
        </Reveal>

        <div className="mt-14 grid gap-px overflow-hidden border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
          {actions.map((a, i) => {
            const Icon = a.icon;
            const inner = (
              <>
                <Icon className="h-7 w-7 text-gold" strokeWidth={1.5} />
                <span className="mt-8 font-display text-xl font-black uppercase tracking-tight text-white md:text-2xl">
                  {a.label}
                </span>
                <span className="mt-2 text-sm text-neutral-400">{a.sub}</span>
                <span className="mt-auto block pt-8">
                  <span className="block h-[3px] w-8 bg-gold transition-[width] duration-500 group-hover:w-full" />
                </span>
              </>
            );
            const cls =
              "group flex h-full flex-col bg-[#0d0d0d] p-8 transition-colors duration-500 hover:bg-charcoal md:p-10";
            return (
              <Reveal key={a.key} delay={i * 0.08} className="bg-[#0d0d0d]">
                {a.external ? (
                  <a data-testid={`contact-${a.key}-btn`} href={a.href} className={cls}>
                    {inner}
                  </a>
                ) : (
                  <Link data-testid={`contact-${a.key}-btn`} to={a.href} className={cls}>
                    {inner}
                  </Link>
                )}
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.15}>
          <div className="mt-14 grid gap-6 md:grid-cols-2" data-testid="contact-two-contacts">
            <div className="border border-white/10 bg-charcoal p-8 md:p-10">
              <p className="font-display text-2xl font-black uppercase tracking-tight text-white">
                R3stan <span className="text-gold">Drywall, Inc.</span>
              </p>
              <p className="mt-4 text-sm font-bold text-white">{biz.contactName}</p>
              <p className="mt-1 text-sm text-neutral-300">
                <a data-testid="contact-drywall-phone" href={biz.phoneHref} className="transition-colors duration-300 hover:text-gold">
                  {biz.phone}
                </a>
              </p>
              <p className="mt-1 text-sm text-neutral-300">
                <a data-testid="contact-drywall-email" href={biz.emailHref} className="transition-colors duration-300 hover:text-gold">
                  {biz.email}
                </a>
              </p>
              <p className="mt-5 border-t border-white/10 pt-4 text-xs leading-relaxed text-neutral-500">
                {t.contact.forDrywall}
              </p>
            </div>
            <div className="border border-white/10 bg-charcoal p-8 md:p-10">
              <p className="font-display text-2xl font-black uppercase tracking-tight text-white">
                R3 <span className="text-gold">Services</span>
              </p>
              <p className="mt-4 text-sm font-bold text-white">{biz.r3ContactName}</p>
              <p className="mt-1 text-sm text-neutral-300">
                <a data-testid="contact-r3-phone" href={biz.phone2Href} className="transition-colors duration-300 hover:text-gold">
                  {biz.phone2}
                </a>
              </p>
              <p className="mt-1 text-sm text-neutral-300">
                <a data-testid="contact-r3-text" href={biz.sms2Href} className="transition-colors duration-300 hover:text-gold">
                  {t.contact.text} · {biz.phone2}
                </a>
              </p>
              <p className="mt-5 border-t border-white/10 pt-4 text-xs leading-relaxed text-neutral-500">
                {t.contact.forR3}
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <p
            data-testid="contact-area-note"
            className="mt-10 text-xs font-bold uppercase tracking-[0.25em] text-neutral-500"
          >
            {t.contact.area}
          </p>
        </Reveal>
      </div>
    </section>
  );
};
